/* global confirm */
define((require) => {
    "use strict";

    const Comment                = require("ui/comments/Comment");
    const CommentCollectionView  = require("ui/comments/CommentCollectionView");
    const ConcatenatedCollection = require("zbone/ConcatenatedCollection");
    const EditorEvents           = require("shared/services/EditorEvents");
    const ImprovedView           = require("editor/zbone/ImprovedView");
    const LoggedIn               = require("shared/security/LoggedIn");
    const Promise                = require("shared/util/Promise");
    const template               = require("ui/comments/CommentView.tpl");
    const Time                   = require("shared/services/Time");

    /**
     * Simple jQuery event handler to stop propagation of the event.
     * @param {jQuery.Event} event The jQuery event.
     */
    const stopPropagation = (event) => (event.stopPropagation());

    /**
     * View of a comment.
     *
     * @author Bruce Mackenzie Nielsen
     * @class CommentView
     * @constructor
     *
     * @cfg {Comment} model The comment model.
     * @cfg {EditorViewModel} viewModel The editor view model.
     */
    return ImprovedView.extend({

        template,

        className: "comment",

        regions: {
            commentCollection: ".comment-collection-region"
        },

        ui: {
            header: ".comment-header",
            commentCollection: ".comment-collection-region",
            replyWrapper: ".comment-reply",
            reply: "textarea",
            replySizer: ".comment-reply-sizer",
            replyIcon: ".comment-reply-icon"
        },

        bindings: {
            ".unread-comments-indicator": "unread",
            ".unread-comments-indicator-wrapper": {
                observe: "unread",
                updateView: false,
                visible(value) {
                    return value > 0;
                }
            }
        },

        events: {
            // Prevent clicks on some elements from dragging the comment
            "mousedown textarea": stopPropagation,
            "mousedown .comment-delete": stopPropagation,
            "mousedown .comment-reply-icon": stopPropagation,

            // Submit comment on enter key, unless control or shift are also pressed
            "keydown textarea": "_textareaKeydownHandler",

            "click .comment-reply-icon": "_submitReply",

            "keyup textarea": "_updateReplySizer",

            "click .comment-delete": "_commentDeleteClickHandler"
        },

        modelEvents: {
            "change:isNew": "_updateNewClass"
        },

        _commentDeleteClickHandler(event) {
            event.preventDefault();

            const isNew = this.model.get("isNew");

            if (isNew) {
                // Empty comment...
                return this.model.destroy();
            }

            if (confirm("Are you sure you want to delete this comment thread?")) {
                // Delete comment thread
                const {viewModel} = this.options;
                const experience = viewModel.get("experience");
                const eventProperties = {
                    commentThreadId: this.model.get("id"),
                    experienceId: experience.get("id"),
                    experienceVersion: experience.get("version"),
                    experiencePushedVersion: experience.get("pushedVersion"),
                    sceneNumber: viewModel.get("sceneNumber"),
                    triggerOrigin: "CommentMenuDeleteButton",
                    numberOfComments: this.model.get("replies").length + 1 /* +1 for counting the comment model 'content' property */
                };

                viewModel.commentDeleted(this.model, this.model.destroy({silent: true}));

                // Trigger only if the comment thread contains comments
                EditorEvents.trigger(EditorEvents.constants.DELETE_COMMENT_THREAD, eventProperties);
            }
        },

        _textareaKeydownHandler(event) {
            if (event.keyCode === 13) {
                if (event.ctrlKey || event.shiftKey) {
                    // REVIEW: Consider simulating an enter if ctrl was pressed, browser appears to ignore it
                    return;
                }

                event.preventDefault();
                this._submitReply();
            }
        },

        _submitReply() {
            const {
                reply,
                commentCollection
            } = this.ui;

            const message = reply.val().trim();

            if (message) {
                let promise;
                const {model} = this;
                const isNew = model.get("isNew");
                const {viewModel} = this.options;
                const experience = viewModel.get("experience");
                const commentData = {
                    createdAt: Time.server(),
                    createdBy: LoggedIn.principal().get("userId"),
                    content:   message
                };

                // Save differently depending on whether or not this is a reply
                if (isNew) {
                    model.set(commentData);
                    promise = model.save();
                } else {
                    const comment = new Comment(commentData);
                    // Make sure the parent comment has been saved...
                    if (model.has("id")) {
                        comment.set("parentId", model.get("id"), {silent: true});
                        promise = comment.save();
                    } else {
                        promise = Promise.deferred((deferred) => {
                            this.listenToOnce(model, "change:id", () => {
                                comment.set("parentId", model.get("id"), {silent: true});
                                deferred.resolve();
                            });
                        });
                    }

                    model.get("replies").add(comment);
                }

                // Signal that a new comment was created
                promise.then(() => {
                    const eventProperties = {
                        commentThreadId: model.get("id"),
                        experienceId: experience.get("id"),
                        experienceVersion: experience.get("version"),
                        experiencePushedVersion: experience.get("pushedVersion"),
                        sceneNumber: viewModel.get("sceneNumber"),
                        messageLength: message.length,
                        isAddedToNewCommentThread: isNew,
                        triggerOrigin: "EditorCommentPlusButton"
                    };

                    EditorEvents.trigger(EditorEvents.constants.CREATE_COMMENT, eventProperties);
                });

                reply.val("");
                this._updateReplySizer();

                viewModel.commentPosted(model, promise);

                commentCollection.scrollTop(commentCollection[0].scrollHeight);
            }
        },

        _sizeCollectionView() {
            // Sets the max height of the collection view to the max height of the entire comment (300) minus the height
            // of the reply area, minus the height of the header, minus the borders (2).
            this.ui.commentCollection.css("max-height", 300 - this.ui.replyWrapper.outerHeight(true) - this.ui.header.outerHeight(true) - 2);
        },

        _updateReplySizer() {
            const {
                reply,
                replySizer,
                replyIcon
            } = this.ui;
            // Copy the textarea text to the hidden div that ensures the sizing
            // Divs render trailing line breaks differently than textareas, so add an extra line break if needed
            let message = reply.val();

            if (!message) {
                message = "\n";
            } else if (message.charAt(message.length - 1) === "\n") {
                message += "\n";
            }

            replySizer.text(message);

            // Detect scroll bar width to set the right position of the icon
            const iconRightPosition = 5 + replySizer.outerWidth() - replySizer.innerWidth();

            replyIcon.css("right", iconRightPosition);
            reply.css("background-position", `bottom 5px right ${iconRightPosition} px`);

            // Update the collection view sizing, since it might now be different
            this._sizeCollectionView();
        },

        _updateNewClass() {
            if (this.model.get("isNew")) {
                this.$el.addClass("comment-new");
            } else {
                this.$el.removeClass("comment-new");
            }
        },

        onRender() {
            this.commentCollection.show(new CommentCollectionView({
                collection: new ConcatenatedCollection(this.model, this.model.get("replies")),
                viewModel: this.options.viewModel
            }));

            this._updateNewClass();
        },

        /**
         * Invoked when the comment view is added to the DOM.
         */
        addedToDOM() {
            this._updateReplySizer();
            this.ui.commentCollection.scrollTop(this.ui.commentCollection[0].scrollHeight);
            this.ui.reply.focus();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/comments/CommentView.js